// ════════════════════════════════════════════════════════════
//  Screens.jsx — Title, How it works, Team Select, Results
// ════════════════════════════════════════════════════════════
//  Plain DOM over a painted backdrop. The cards are the canon card
//  art (public/cards, made from Assets/terror-assets by
//  tools/make-cards.py) — never redrawn, never invented.
// ════════════════════════════════════════════════════════════
import IEGlyph from "../IEGlyph.js";
import { useState, useMemo } from "react";
import { LINES, LINE_IDS, TIER_ORDER, TIER_LABEL, IMPERA_UNLOCK, PRIMERA, cardUrl, portraitUrl, tierStats } from "../data/roster.js";
import { ARTS, TOME_IDS, TOME_SLOTS, DEFAULT_TOMES, CAST_TIME, tomeUrl } from "../data/arts.js";
import { rivalTeam, TEAM_SIZE } from "../sim/battle.js";
import { RULEBOOK, RULEBOOK_VERSION } from "../data/rulebook.js";
import { say } from "../data/text.js";

export const GAME_TITLE = "SHATTERPROOF";   // caP'n named it, 2026-10-15

export function Title({ onPlay, onWatch, onHow, onOnline, onClimb }) {
  const [pick, setPick] = useState(false);   // Battle opens a small choice: fight or watch (caP'n, 2026-10-16)
  return (
    <div className="screen title-screen">
      <div className="eclipse"><div className="crack" /></div>
      <div className="title-block">
        <div className="title-kicker">{say("title.kicker")}</div>
        <h1 className="title-main"><span>{say("title.name.small")}</span><b>{say("title.name.big")}</b></h1>
        <div className="title-sub">{say("title.subtitle")}</div>
        <div className="title-buttons">
          {pick ? (<>
            <button className="btn primary" onClick={onPlay}>{say("title.button.fight")}</button>
            {onClimb && <button className="btn" onClick={onClimb}>{say("title.button.climb")}</button>}
            <button className="btn" onClick={onWatch}>{say("title.button.watch")}</button>
            <button className="btn ghost" onClick={() => setPick(false)}>{say("title.button.back")}</button>
          </>) : (<>
            <button className="btn primary" onClick={() => setPick(true)}>{say("title.button.battle")}</button>
            {onOnline && <button className="btn online-btn" onClick={onOnline}>{say("title.button.online")}</button>}
            <button className="btn ghost" onClick={onHow}>{say("title.button.rulebook")}</button>
          </>)}
        </div>
      </div>
    </div>
  );
}

// THE RULEBOOK — every number comes from the engine (src/data/rulebook.js)
const rich = (t) => t.split(/(\*\*[^*]+\*\*)/).map((x, i) => (x.startsWith("**") ? <b key={i}>{x.slice(2, -2)}</b> : x));
export function HowTo({ onBack }) {
  const [at, setAt] = useState(RULEBOOK[0].id);
  const sec = RULEBOOK.find((x) => x.id === at) || RULEBOOK[0];
  return (
    <div className="screen howto rulebook">
      <div className="panel">
        <div className="rb-head"><h2>The Shatterproof Rulebook</h2><button className="btn primary" onClick={onBack}>Got it</button></div>
        <div className="rb-body">
          <nav className="rb-nav">
            {RULEBOOK.map((x, i) => (
              <button key={x.id} className={"rb-tab" + (x.id === at ? " on" : "")} onClick={() => setAt(x.id)}><i>{i + 1}</i>{x.title}</button>
            ))}
          </nav>
          <div className="rb-page">
            <h3>{sec.title}</h3>
            <ul>{sec.items.map((t, i) => <li key={i}>{rich(t)}</li>)}</ul>
          </div>
        </div>
        <div className="rb-foot">Rulebook {RULEBOOK_VERSION} · numbers read live from the game</div>
      </div>
    </div>
  );
}

export function TeamSelect({ onStart, onBack, initial = [], initialTomes = DEFAULT_TOMES, goLabel = null }) {
  const clean = (initial || []).filter((id) => LINES[id]).slice(0, TEAM_SIZE);
  const cleanT = (initialTomes || []).filter((id) => ARTS[id] && ARTS[id].kind === "tome").slice(0, TOME_SLOTS);
  const [team, setTeam] = useState(clean);
  const [tomes, setTomes] = useState(cleanT.length === TOME_SLOTS ? cleanT : DEFAULT_TOMES);
  const [focus, setFocus] = useState(clean[0] || "spectral_tiger");
  const [tab, setTab] = useState("beings");
  const [tomeFocus, setTomeFocus] = useState(tomes[0]);
  const toggle = (id) => { setFocus(id); setTeam((t) => (t.includes(id) ? t.filter((x) => x !== id) : t.length < TEAM_SIZE ? [...t, id] : t)); };
  const toggleTome = (id) => { setTomeFocus(id); setTomes((t) => (t.includes(id) ? t.filter((x) => x !== id) : t.length < TOME_SLOTS ? [...t, id] : t)); };
  const randomize = () => { const r = rivalTeam((Date.now() & 0xffff) + 1); setTeam(r); setFocus(r[0]); };
  const L = LINES[focus];
  const ladder = useMemo(() => TIER_ORDER.map((t) => ({ t, ...tierStats(focus, t) })), [focus]);
  const ready = team.length === TEAM_SIZE && tomes.length === TOME_SLOTS;
  const ta = ARTS[tomeFocus];
  return (
    <div className="screen teamselect">
      <div className="ts-head">
        <button className="btn ghost" onClick={onBack}>← Back</button>
        <div className="ts-tabs">
          <button className={"tab" + (tab === "beings" ? " on" : "")} onClick={() => setTab("beings")}>Beings {team.length}/{TEAM_SIZE}</button>
          <button className={"tab" + (tab === "tomes" ? " on" : "")} onClick={() => setTab("tomes")}>Tomes {tomes.length}/{TOME_SLOTS}</button>
        </div>
        <div className="ts-actions">
          {tab === "beings" && <button className="btn" onClick={randomize}>Random three</button>}
          <button className="btn primary" disabled={!ready} onClick={() => onStart(team, tomes)}>{goLabel || "To the Shatterfield ▸"}</button>
        </div>
      </div>
      <div className="ts-team">
        {Array.from({ length: TEAM_SIZE }, (_, i) => {
          const id = team[i];
          return (
            <div key={"b" + i} className={"ts-slot" + (id ? " filled" : "")} onClick={() => id && toggle(id)}>
              {id ? <><span className="ts-slot-art" style={{ backgroundImage: `url(${portraitUrl(id, "beb")})` }} /><span>{LINES[id].lord}</span></> : <span className="ts-slot-n">{i + 1}</span>}
            </div>
          );
        })}
        {Array.from({ length: TOME_SLOTS }, (_, i) => {
          const id = tomes[i];
          return (
            <div key={"t" + i} className={"ts-slot tome" + (id ? " filled" : "")} onClick={() => { setTab("tomes"); if (id) toggleTome(id); }}>
              {id ? <><span className="ts-slot-art sq" style={{ backgroundImage: `url(${tomeUrl(id)})` }} /><span>{ARTS[id].name}</span></> : <span className="ts-slot-n">{"ZXC"[i]}</span>}
            </div>
          );
        })}
      </div>
      {tab === "beings" ? (
        <div className="ts-body">
          <div className="ts-grid">
            {LINE_IDS.map((id) => {
              const n = team.indexOf(id);
              return (
                <button key={id} className={"ts-card" + (n >= 0 ? " picked" : "") + (focus === id ? " focus" : "")} onClick={() => toggle(id)} onMouseEnter={() => setFocus(id)}>
                  <img src={cardUrl(id, "beb")} alt={LINES[id].tiers.beb.title} loading="lazy" />
                  {n >= 0 && <span className="ts-n">{n + 1}</span>}
                </button>
              );
            })}
          </div>
          <div className="ts-info">
            <div className="ts-info-lord">{L.lord}</div>
            <div className="ts-info-line">{L.name} <span>· {L.kin} · {L.ilk} · Primera: {PRIMERA[L.ilk].name}</span></div>
            <div className="ts-ladder">
              {ladder.map((x) => (
                <div key={x.t} className="ts-rung">
                  <img src={cardUrl(focus, x.t)} alt={x.title} loading="lazy" />
                  <b>{TIER_LABEL[x.t]}</b>
                  <span>Lyf {x.lyf} · Lyt {x.lyt} · Anima {x.anima}</span>
                </div>
              ))}
            </div>
            <div className="ts-moves">
              <div className="ts-move"><div className="ts-move-head"><b>{L.lytName}</b><span>Lyt Attack · {L.lyt === "shot" ? "a shot" : "a lunge"}</span></div></div>
              <div className="ts-move"><div className="ts-move-head"><b>{PRIMERA[L.ilk].name}</b><span>Primera · {L.ilk}</span></div><div className="ts-move-card">{PRIMERA[L.ilk].note}</div></div>
              {[...L.impera, L.uhlti].map((id, i) => {
                const a = ARTS[id];
                return (
                  <div key={id} className={"ts-move" + (a.kind === "uhlti" ? " ult" : "")}>
                    <div className="ts-move-head"><b>{a.name}</b><span>{a.kind === "uhlti" ? "U'hlti · Lord" : `Impera ${"I".repeat(i + 1)} · ${TIER_LABEL[IMPERA_UNLOCK[i]]}`} · {a.khao} Khao{a.anima ? ` + ${a.anima} Anima` : ""}</span></div>
                    <div className="ts-move-card">{a.card}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="ts-body">
          <div className="ts-grid tomes">
            {TOME_IDS.map((id) => {
              const n = tomes.indexOf(id);
              return (
                <button key={id} className={"ts-card" + (n >= 0 ? " picked" : "") + (tomeFocus === id ? " focus" : "")} onClick={() => toggleTome(id)} onMouseEnter={() => setTomeFocus(id)}>
                  <img src={tomeUrl(id)} alt={ARTS[id].name} loading="lazy" />
                  {n >= 0 && <span className="ts-n">{"ZXC"[n]}</span>}
                </button>
              );
            })}
          </div>
          <div className="ts-info">
            <div className="ts-info-lord">{ta.name}</div>
            <div className="ts-info-line">{ta.school} Tome <span>· Level {ta.level} · {ta.khao} Khao{ta.anima ? ` + ${ta.anima} Anima` : ""} · {CAST_TIME[ta.level] ? `${CAST_TIME[ta.level]}s to appear` : "instant"} · 10s rest</span></div>
            <img className="ts-tome-card" src={tomeUrl(tomeFocus)} alt={ta.name} />
            <div className="ts-move-card big">{ta.card}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Results({ result, onRematch, onTeam, onTitle }) {
  const { won, why, stats, mine, theirs, spectate } = result;
  const whyText = why === "called" ? ({ beings: "called: more beings standing", anima: "called: more Anima left", resurrections: "called: fewer resurrections", lyf: "called: more Lyf left", coin: "called: dead even, the coin decided" }[result.by] || "the duel was called") : "every being was ruined";
  return (
    <div className="screen results">
      <div className={"res-banner " + (spectate ? "watch" : won ? "win" : "lose")}>
        {spectate ? (won ? "BLUE AENARCHIST WINS" : "RED AENARCHIST WINS") : won ? "VICTORY" : "DEFEAT"}
        <small>{whyText}</small>
      </div>
      <div className="res-teams">
        {[mine, theirs].map((side, k) => (
          <div key={k} className="res-team">
            <div className="res-team-name">{side.name}</div>
            <div className="res-row">
              {side.roster.map((r) => (
                <div key={r.idx} className={"res-being" + (r.ruined ? " ruined" : "")}>
                  <span style={{ backgroundImage: `url(${portraitUrl(r.line, r.tier)})` }} />
                  <em>{LINES[r.line].lord}</em><small>{TIER_LABEL[r.tier]}</small>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="res-stats">
        <span>⏱ {Math.floor(stats.time / 60)}:{String(Math.floor(stats.time % 60)).padStart(2, "0")}</span>
        <span><IEGlyph kind="heart" size={22} /> {stats.transcends} Transcendences</span>
        <span>⚔ {stats.clashes} Clashes</span>
        <span>☄ {stats.ults} U'hltis</span>
        <span>💥 {stats.biggest} biggest hit</span>
      </div>
      <div className="title-buttons">
        <button className="btn primary" onClick={onRematch}>Rematch</button>
        {!spectate && <button className="btn" onClick={onTeam}>Change team</button>}
        <button className="btn ghost" onClick={onTitle}>Title</button>
      </div>
    </div>
  );
}
